import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "./LanguageContext";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  // Read token from URL
  const query = new URLSearchParams(useLocation().search);
  const token = query.get("token");

  // --- i18n helpers ---
  const TXT = useMemo(
    () => ({
      title: language === "en" ? "Reset Password" : "პაროლის აღდგენა",
      newPw: language === "en" ? "New Password:" : "ახალი პაროლი:",
      confirmPw: language === "en" ? "Confirm Password:" : "გაიმეორეთ პაროლი:",
      submit: language === "en" ? "Reset Password" : "პაროლის აღდგენა",
      invalidLink: language === "en" ? "Invalid or expired reset link." : "არასწორი ან ვადაგასული ბმული.",
      tokenInvalid: language === "en" ? "Invalid or expired token." : "არასწორი ან ვადაგასული ბმული.",
      pwMismatch: language === "en" ? "Passwords do not match." : "პაროლები არ ემთხვევა.",
      pwSuccess: language === "en"
        ? "Password reset successful! Redirecting, now you can log in..."
        : "პაროლი წარმატებით განახლდა! გადამისამართება, ახლა შეგიძლიათ შეხვიდეთ...",
      pwFail: language === "en" ? "Password reset failed." : "პაროლის განახლება ვერ მოხერხდა.",
      somethingWrong: language === "en" ? "Something went wrong. Please try again." : "დაფიქსირდა შეცდომა, გთხოვთ სცადოთ თავიდან.",
      // Requirements text
      reqTitle: language === "en" ? "Password must contain:" : "პაროლი უნდა შეიცავდეს:",
      reqLen: language === "en" ? "At least 8 characters" : "მინიმუმ 8 სიმბოლო",
      reqUpper: language === "en" ? "At least 1 uppercase letter (A-Z)" : "მინიმუმ 1 დიდი ასო (A-Z)",
      reqSymbol: language === "en" ? "At least 1 symbol (e.g. !@#$%^&*)" : "მინიმუმ 1 სიმბოლო ( напр. !@#$%^&*)",
      confirmMatch: language === "en" ? "Passwords match" : "პაროლები ემთხვევა",
    }),
    [language]
  );

  useEffect(() => {
    if (!token) {
      setError(TXT.invalidLink);
    }
  }, [token, TXT.invalidLink]);

  // --- Validation logic ---
  const hasMinLen = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;

  const isPwValid = hasMinLen && hasUpper && hasSymbol;
  const canSubmit = token && isPwValid && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Final guard on submit
    if (!isPwValid) {
      setError(TXT.pwFail);
      return;
    }
    if (!passwordsMatch) {
      setError(TXT.pwMismatch);
      return;
    }

    try {
      const response = await api.post(`${API_BASE_URL}/reset_password.php`, { token, password });
      if (response.data?.success) {
        setSuccess(TXT.pwSuccess);
        setTimeout(() => navigate("/"), 3000);
      } else {
        setError(response.data?.error || TXT.pwFail);
      }
    } catch (err) {
      setError(TXT.somethingWrong);
    }
  };

  // Simple styles for requirement pills
  const pill = (ok) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 14,
    background: ok ? "rgba(0,180,0,0.08)" : "rgba(220,0,0,0.07)",
    border: `1px solid ${ok ? "rgba(0,180,0,0.35)" : "rgba(220,0,0,0.35)"}`,
  });

  const icon = (ok) => (
    <span aria-hidden="true" style={{ fontWeight: 700 }}>
      {ok ? "✓" : "•"}
    </span>
  );

  return (
    <div className="reset-password" style={{ maxWidth: 520, margin: "24px auto", padding: 16 }}>
      <h2 style={{ textAlign: "center", marginBottom: 12 }}>{TXT.title}</h2>

      {error && <p role="alert" style={{ color: "red", marginBottom: 10 }}>{error}</p>}
      {success && <p role="status" style={{ color: "green", marginBottom: 10 }}>{success}</p>}

      {token ? (
        <>
          <form onSubmit={handleSubmit} noValidate>
            <label style={{ display: "block", marginBottom: 6 }}>{TXT.newPw}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              aria-invalid={isPwValid ? "false" : "true"}
              aria-describedby="pw-reqs"
              style={{ width: "95%", padding: 10, marginBottom: 12, borderRadius: 8, border: "1px solid #ccc" }}
            />

            <div id="pw-reqs" style={{ display: "grid", gap: 8, marginBottom: 14 }}>
              <strong style={{ fontSize: 13 }}>{TXT.reqTitle}</strong>
              <div style={pill(hasMinLen)}>{icon(hasMinLen)} {TXT.reqLen}</div>
              <div style={pill(hasUpper)}>{icon(hasUpper)} {TXT.reqUpper}</div>
              <div style={pill(hasSymbol)}>{icon(hasSymbol)} {TXT.reqSymbol}</div>
            </div>

            <label style={{ display: "block", marginBottom: 6 }}>{TXT.confirmPw}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              aria-invalid={passwordsMatch ? "false" : "true"}
              style={{ width: "100%", padding: 10, marginBottom: 8, borderRadius: 8, border: "1px solid #ccc" }}
            />

            {confirmPassword.length > 0 && (
              <div style={{ ...pill(passwordsMatch), marginBottom: 14 }}>
                {icon(passwordsMatch)} {TXT.confirmMatch}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid #111",
                background: canSubmit ? "#111" : "#999",
                color: "#fff",
                cursor: canSubmit ? "pointer" : "not-allowed",
                fontWeight: 600,
              }}
            >
              {TXT.submit}
            </button>
          </form>
        </>
      ) : (
        <p style={{ color: "red" }}>{TXT.tokenInvalid}</p>
      )}
    </div>
  );
};

export default ResetPassword;
